import { Component, OnDestroy } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { TIME_PERIODS } from '../../../constants/time-periods';
import { DashboardService } from '../../services/dashboard.service';
import { debounceTime, Subscription } from 'rxjs';
import { featuresConstants } from '../../../constants/features.constants';
import { PermissionService } from '../../../services/permission.service';
import { FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: 'dashboard.component.html',
  styleUrl: 'dashboard.component.scss',
})
export class DashboardComponent implements OnDestroy {
  Features = featuresConstants;

  private activeCallsSubscription?: Subscription;
  timePeriods = TIME_PERIODS;
  selectedClientControl = new FormControl<string[]>([]);
  clientsList: Array<{ clientId: string; name: string }> = [];

  cards = [
    {
      label: 'Total Calls',
      icon: 'Phone-Call-Answer-Icon.svg',
      iconBgColor: 'bg-[#0D9488]',
      percentage: 0,
      total: 0,
      key: this.Features.dashboard.cards.totalCalls,
    },
    {
      label: 'Outgoing Calls',
      icon: 'Phone-Call-Answer-Icon.svg',
      iconBgColor: 'bg-[#0D9488]',
      percentage: 0,
      total: 0,
      key: this.Features.dashboard.cards.outgoingCalls,
    },
    {
      label: 'In-Progress Calls',
      icon: 'Phone-Call-Answer-Icon.svg',
      iconBgColor: 'bg-[#0D9488]',
      percentage: 0,
      total: 0,
      key: this.Features.dashboard.cards.inprogressCalls,
    },
    {
      label: 'Active Calls',
      icon: 'Phone-Call-Answer-Icon.svg',
      iconBgColor: 'bg-[#0D9488]',
      percentage: 0,
      total: 0,
      key: this.Features.dashboard.cards.activeCalls,
    },
  ];

  constructor(
    private dashboardService: DashboardService,
    // private authService: AuthService,
    private permissionService: PermissionService,
    private datePipe: DatePipe,
    private authService: AuthService
  ) {
    this.fetchClientsList();
  }

  ngAfterViewInit(): void {
    this.onClientChange();
    this.permissionService.getPermission().subscribe({
      next: (res) => {
        if (res) {
          // Check if user has permission
          // const hasPermission = Object.keys(res).some(
          //   (p) =>
          //     (res as any)[p]?.[featuresConstants.dashboard.type]?.[
          //       featuresConstants.dashboard.cards.activeCalls
          //     ]
          // );
          const screens = this.permissionService.getScreenPermissionsData();
          const hasPermission = screens?.dashboard;
          hasPermission && this.subscribeToActiveCallsUpdates();
          const clientsList = this.selectedClientControl.value;
          this.fetchCardData('last30Days', clientsList);
          this.fetchConnectionRateGraphData('last30Days', clientsList);
          this.fetchOutgoingGraphData('last30Days', clientsList);
          this.fetchConcurrentCallsGraphData('last30Days', clientsList);
        }
      },
    });
  }

  // Add this method to handle time period changes
  onTimePeriodChange(event: Event, sectionName: string): void {
    const target = event.target as HTMLSelectElement;
    const selectedTimePeriod = target.value;
    const clientsList = this.selectedClientControl.value;
    switch (sectionName.toLowerCase()) {
      case 'card':
        this.fetchCardData(selectedTimePeriod, clientsList);
        break;
      case 'outbound calls':
        this.fetchOutgoingGraphData(selectedTimePeriod, clientsList);
        break;
      case 'connection rate':
        this.fetchConnectionRateGraphData(selectedTimePeriod, clientsList);
        break;
      case 'concurrent calls':
        this.fetchConcurrentCallsGraphData(selectedTimePeriod, clientsList);
        break;
      default:
        console.warn(
          'Unknown section name for time period change:',
          sectionName
        );
        break;
    }
  }

  onClientChange(): void {
    this.selectedClientControl.valueChanges.pipe(debounceTime(800)).subscribe({
      next: (res) => {
        if (res && res[0] == null) {
          return;
        }
        this.fetchCardData('last30Days', res);
        this.fetchConnectionRateGraphData('last30Days', res);
        this.fetchOutgoingGraphData('last30Days', res);
        this.fetchConcurrentCallsGraphData('last30Days', res);
      },
    });
  }

  selectAllClient(): void {
    const payload = this.clientsList.map((c) => c.clientId);
    this.selectedClientControl.patchValue(payload, {
      emitEvent: false,
    });
    this.fetchCardData('last30Days', payload);
    this.fetchConnectionRateGraphData('last30Days', payload);
    this.fetchOutgoingGraphData('last30Days', payload);
    this.fetchConcurrentCallsGraphData('last30Days', payload);
  }

  subscribeToActiveCallsUpdates(): void {
    this.activeCallsSubscription = this.dashboardService
      .getActiveCallsStream()
      .subscribe({
        next: (data) => {
          if (data.type === 'active_call_count') {
            // Update the Active Calls card (index 3)
            this.cards[3].total = data.active_call_count;
            this.cards[2].total = data.active_call_count;
          }
        },
      });
  }

  private fetchClientsList(): void {
    // const filters: Filters<string> = {
    //   filter: '',
    //   pageNo: 1,
    //   pageSize: 100000,
    //   searchText: '',
    //   sortBy: '',
    //   sortOrder: 'asc',
    // };
    // this.clientService.fetchClientList(filters).subscribe({
    //   next: (res) => {
    //     this.clientsList = res.data.list;
    //   },
    // });
    this.clientsList = this.authService
      .getProperty('data')
      .map((c: any) => ({ clientId: c.clientId[0], name: c.clientName }));
  }

  fetchCardData(selectedTimePeriod: string = 'last30Days', payload: any): void {
    const cards = Object.values(featuresConstants.dashboard.cards);
    const has = cards.some((c) =>
      this.permissionService.hasPermissionToAnyone('dashboard', c)
    );
    if (!has) return;
    this.dashboardService.getCallCounts(selectedTimePeriod, payload).subscribe({
      next: (response) => {
        this.cards[0].total = response.data.totalCalls || 0;
        this.cards[1].total = response.data.outgoingCalls || 0;
        this.cards[2].total = response.data.inProgressCalls || 0;
        // this.cards[3].total = response.data.inProgressCalls || 0;
      },
    });
  }

  fetchOutgoingGraphData(
    selectedTimePeriod: string = 'last30Days',
    payload: any
  ): void {
    if (
      !this.permissionService.hasPermissionToAnyone(
        'dashboard',
        featuresConstants.dashboard.graphs.outboundCallsByDate
      )
    )
      return;
    this.dashboardService
      .getOutboundCalls(selectedTimePeriod, payload)
      .subscribe({
        next: (response) => {
          const { labels, data } = response.data.reduce(
            (acc, item) => {
              acc.labels.push(this.formatDate(item.date));
              acc.data.push(Number(item.count) || 0);
              return acc;
            },
            { labels: [] as string[], data: [] as number[] }
          );

          this.outboundChartData.datasets[0].data = data;

          this.outboundChartData = {
            labels: labels,
            datasets: this.outboundChartData.datasets,
          };
        },
      });
  }

  fetchConnectionRateGraphData(
    selectedTimePeriod: string = 'last30Days',
    payload: any
  ): void {
    if (
      !this.permissionService.hasPermissionToAnyone(
        'dashboard',
        featuresConstants.dashboard.graphs.connectionRateByDate
      )
    )
      return;
    this.dashboardService
      .getConnectionRate(selectedTimePeriod, payload)
      .subscribe({
        next: (response) => {
          const { labels, data } = response.data.reduce(
            (acc, item) => {
              acc.labels.push(this.formatDate(item.date));
              acc.data.push(Number(item.percentage) || 0);
              return acc;
            },
            { labels: [] as string[], data: [] as number[] }
          );
          this.connectionRateChartData.datasets[0].data = data;

          this.connectionRateChartData = {
            labels: labels,
            datasets: this.connectionRateChartData.datasets,
          };
        },
      });
  }

  fetchConcurrentCallsGraphData(
    selectedTimePeriod: string = 'last30Days',
    payload: any
  ): void {
    if (
      !this.permissionService.hasPermissionToAnyone(
        'dashboard',
        featuresConstants.dashboard.graphs.concurrentCallsByDate
      )
    )
      return;
    this.dashboardService
      .getConcurrentCalls(selectedTimePeriod, payload)
      .subscribe({
        next: (response) => {
          const formatLabel = (date: string) => {
            switch (selectedTimePeriod) {
              case 'today':
                return this.datePipe.transform(date, 'hh a')!;
              case 'yesterday':
                return this.datePipe.transform(date, 'MMM dd - HH a')!;
              default:
                return this.formatDate(date);
            }
          };

          const grouped = (response?.data ?? []).reduce(
            (acc, { date, count }) => {
              const key = formatLabel(date);
              acc.map[key] = Math.max(acc.map[key] || 0, Number(count) || 0);
              if (!acc.labels.includes(key)) {
                acc.labels.push(key);
              }
              return acc;
            },
            {
              labels: [] as string[],
              map: {} as Record<string, number>,
            }
          );

          // For sorting (works for both "today" and "yesterday")
          const to24Hour = (label: string) => {
            const timePart = label.includes('-')
              ? label.split('-')[1].trim()
              : label;
            const [time, period] = timePart.split(' ');
            let hour = +time.split(':')[0];
            if (period === 'PM' && hour !== 12) hour += 12;
            if (period === 'AM' && hour === 12) hour = 0;
            return hour;
          };

          // Only sort when time-based
          const labels =
            selectedTimePeriod === 'today' || selectedTimePeriod === 'yesterday'
              ? grouped.labels.sort((a, b) => to24Hour(a) - to24Hour(b))
              : grouped.labels;

          const data = labels.map((label) => grouped.map[label]);
          this.concurrentCallsChartData.datasets[0].data = data;

          // Assign to chart
          this.concurrentCallsChartData = {
            labels,
            datasets: this.concurrentCallsChartData.datasets,
          };
        },
      });
  }

  // ADDED: Helper function to format dates from API (e.g., "2025-10-28T00:00:00" to "Oct 28")
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  }

  // 1️⃣ Outbound Calls (Line Chart)
  public outboundAndConcurrentChartOptions: ChartConfiguration<'line'>['options'] =
    {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false } },
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: number | string) => {
              const num = Number(value);

              if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
              if (num >= 1_000) return (num / 1_000).toFixed(0) + 'K';

              return num.toString();
            },
          },
        },
      },
    };

  public outboundChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Outbound Calls',
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59,130,246,0.2)',
        tension: 0.4,
        fill: true,
        borderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  public concurrentCallsChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Concurrent Calls',
        borderColor: '#f2992c',
        backgroundColor: 'rgba(250, 191, 120,0.4)',
        tension: 0.4,
        fill: true,
        borderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  // 2️⃣ Connection Rate (Line Chart)
  public connectionRateChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: true } },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v + '%' },
      },
    },
    elements: {
      line: { tension: 0.1, borderWidth: 2 },
      point: { radius: 3, hoverRadius: 6 },
    },
  };

  public connectionRateChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Connection Rate (%)',
        borderColor: '#AE4787',
        backgroundColor: 'rgba(174,71,135,0.2)',
        fill: true,
        tension: 0.3,
        borderWidth: 3,
      },
    ],
  };

  // private sharedService = inject(SharedService);
  // openFlowDiagram(): void {
  //   this.sharedService.openDialogBox(FlowDesignerComponent, {
  //     width: '100%',
  //   });
  // }

  ngOnDestroy(): void {
    // Clean up WebSocket connection when component is destroyed
    if (this.activeCallsSubscription) {
      this.activeCallsSubscription.unsubscribe();
    }
    this.dashboardService.closeActiveCallsStream();
  }
}
